import { useState, useEffect } from 'react';
import { Button } from '@/components/common/button';
import { Input } from '@/components/common/input';
import { Modal } from '@/components/common/modal';
import {
  Calendar,
  DollarSign,
  CreditCard,
  AlertCircle,
  FileText,
  Banknote,
  Building,
  Smartphone,
  Receipt as ReceiptIcon,
  Hash
} from 'lucide-react';
import { CreatePaymentPayload, PaymentMethod } from '@/types/payment_types';
import { Invoice } from '@/types/invoice_types';

interface PaymentFormProps {
  is_open: boolean;
  on_close: () => void;
  on_submit: (data: CreatePaymentPayload) => void;
  invoices: Invoice[];
  is_loading?: boolean;
  pre_selected_client_id?: number;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  is_open,
  on_close,
  on_submit,
  invoices,
  is_loading = false,
  pre_selected_client_id
}) => {
  const [form_data, set_form_data] = useState<CreatePaymentPayload>({
    invoice_id: 0,
    amount: 0,
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: '',
    status: 'completed',
    client_id:0
  });

  const [errors, set_errors] = useState<Record<string, string>>({});
  const [selected_invoice, set_selected_invoice] = useState<Invoice | null>(null);

  // Get filtered invoices based on pre-selected client
  const filtered_invoices = pre_selected_client_id
    ? invoices.filter(inv => 
        inv.client?.toString() === pre_selected_client_id?.toString() && 
        parseFloat((inv as any).amount_outstanding) > 0
      )
    : invoices.filter(inv => parseFloat((inv as any).amount_outstanding) > 0);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (is_open) {
      set_form_data({
        invoice_id: 0,
        amount: 0,
        payment_method: 'cash',
        payment_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: '',
        status: 'completed',
        client_id:0
      });
      set_selected_invoice(null);
      set_errors({});
    }
  }, [is_open]);

  const validate_form = (): boolean => {
    const new_errors: Record<string, string> = {};

    if (!form_data.invoice_id || form_data.invoice_id === 0) {
      new_errors.invoice_id = 'Invoice is required';
    }

    if (!form_data.amount || form_data.amount <= 0) {
      new_errors.amount = 'Amount must be greater than 0';
    }

    if (selected_invoice && form_data.amount > parseFloat((selected_invoice as any).amount_outstanding)) {
      new_errors.amount = `Amount cannot exceed balance due (${format_currency(parseFloat((selected_invoice as any).amount_outstanding))})`;
    }

    if (!form_data.payment_date) {
      new_errors.payment_date = 'Payment date is required';
    } else {
      const payment_date = new Date(form_data.payment_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (payment_date > today) {
        new_errors.payment_date = 'Payment date cannot be in the future';
      }
    }

    if (form_data.payment_method === 'mpesa' && !form_data.reference_number?.trim()) {
      new_errors.reference_number = 'M-Pesa transaction ID is required';
    }

    if (form_data.payment_method === 'cheque' && !form_data.reference_number?.trim()) {
      new_errors.reference_number = 'Cheque number is required';
    }

    if (form_data.payment_method === 'bank_transfer' && !form_data.reference_number?.trim()) {
      new_errors.reference_number = 'Bank reference number is required';
    }

    set_errors(new_errors);
    return Object.keys(new_errors).length === 0;
  };

  const handle_submit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate_form()) {
      const payload = {
        ...form_data,
        client_id: selected_invoice?.client
      };
      on_submit(payload as any);
    }
  };

  const handle_change = (field: keyof CreatePaymentPayload, value: any) => {
    set_form_data(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      set_errors(prev => {
        const new_errors = { ...prev };
        delete new_errors[field];
        return new_errors;
      });
    }
  };

  const handle_invoice_change = (invoice_id: string) => {
    const invoice_id_num = parseInt(invoice_id);
    handle_change('invoice_id', invoice_id_num);
    const invoice = invoices.find(inv => inv.id === invoice_id_num);
    set_selected_invoice(invoice || null);

    if (invoice) {
      handle_change('amount', parseFloat((invoice as any).amount_outstanding));
    }
  };

  const handle_close = () => {
    if (!is_loading) {
      on_close();
    }
  };

  const format_currency = (amount: number) => {
    return `KES ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const payment_methods: { value: PaymentMethod; label: string; icon: React.ReactNode }[] = [
    { value: 'cash', label: 'Cash', icon: <Banknote size={24} /> },
    { value: 'mpesa', label: 'M-Pesa', icon: <Smartphone size={24} /> },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: <Building size={24} /> },
    { value: 'cheque', label: 'Cheque', icon: <ReceiptIcon size={24} /> },
    { value: 'card', label: 'Card', icon: <CreditCard size={24} /> }
  ];

  return (
    <Modal
      is_open={is_open}
      on_close={handle_close}
      title="Record Payment"
      size="lg"
      footer={
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={handle_close}
            disabled={is_loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handle_submit}
            is_loading={is_loading}
          >
            Record Payment
          </Button>
        </div>
      }
    >
      <form onSubmit={handle_submit} className="space-y-6">
        {/* Invoice Selection */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Invoice Information
          </h3>

          <div>
            <label className="block text-sm font-medium text-charcoal mb-1.5">
              Select Invoice <span className="text-error">*</span>
            </label>
            <select
              value={form_data.invoice_id}
              onChange={(e) => handle_invoice_change(e.target.value)}
              className="w-full px-4 py-2.5 border border-light-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all bg-white text-charcoal"
            >
              <option value={0}>Select an invoice...</option>
              {filtered_invoices.map(invoice => (
                <option key={invoice.id} value={invoice.id}>
                  {invoice.invoice_number} - {invoice.client_name} - Balance: {format_currency(parseFloat((invoice as any).amount_outstanding))}
                </option>
              ))}
            </select>
            {errors.invoice_id && (
              <p className="mt-1.5 text-sm text-error flex items-center gap-1">
                <AlertCircle size={14} />
                {errors.invoice_id}
              </p>
            )}
          </div>

          {/* Selected Invoice Details */}
          {selected_invoice && (
            <div className="p-5 bg-forest-green bg-opacity-5 rounded-lg border border-forest-green">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-medium-gray font-medium mb-1">Client</p>
                  <p className="text-sm font-semibold text-charcoal">{selected_invoice.client_name}</p>
                </div>
                <div>
                  <p className="text-xs text-medium-gray font-medium mb-1">Invoice Total</p>
                  <p className="text-sm font-semibold text-charcoal">
                    {format_currency(parseFloat((selected_invoice as any).total_amount))}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-medium-gray font-medium mb-1">Amount Paid</p>
                  <p className="text-sm font-semibold text-success">
                    {format_currency(parseFloat((selected_invoice as any).amount_paid || '0'))}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-medium-gray font-medium mb-1">Balance Due</p>
                  <p className="text-sm font-bold text-error">
                    {format_currency(parseFloat((selected_invoice as any).amount_outstanding))}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Method Selection */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Payment Method
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {payment_methods.map(method => (
              <button
                key={method.value}
                type="button"
                onClick={() => handle_change('payment_method', method.value)}
                className={`p-4 border-2 rounded-lg transition-all ${
                  form_data.payment_method === method.value
                    ? 'border-forest-green bg-forest-green bg-opacity-5 shadow-sm'
                    : 'border-light-gray hover:border-medium-gray hover:bg-off-white'
                }`}
              >
                <div className={`mx-auto mb-2 ${
                  form_data.payment_method === method.value
                    ? 'text-forest-green'
                    : 'text-medium-gray'
                }`}>
                  {method.icon}
                </div>
                <p className={`text-sm font-medium ${
                  form_data.payment_method === method.value
                    ? 'text-forest-green'
                    : 'text-charcoal'
                }`}>
                  {method.label}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Payment Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Payment Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Payment Amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={form_data.amount || ''}
              onChange={(e) => handle_change('amount', parseFloat(e.target.value) || 0)}
              icon_left={<DollarSign size={18} />}
              error={errors.amount}
              is_required
              helper_text={selected_invoice ? `Balance due: ${format_currency(parseFloat((selected_invoice as any).amount_outstanding))}` : ''}
            />

            <Input
              label="Payment Date"
              type="date"
              value={form_data.payment_date}
              onChange={(e) => handle_change('payment_date', e.target.value)}
              icon_left={<Calendar size={18} />}
              error={errors.payment_date}
              is_required
            />
          </div>

          {/* Conditional Fields Based on Payment Method */}
          {form_data.payment_method === 'mpesa' && (
            <Input
              label="M-Pesa Transaction ID"
              placeholder="e.g., QGH7XYZ123"
              value={form_data.reference_number}
              onChange={(e) => handle_change('reference_number', e.target.value)}
              icon_left={<Hash size={18} />}
              error={errors.reference_number}
              is_required
              helper_text="Enter the M-Pesa confirmation code"
            />
          )}

          {form_data.payment_method === 'cheque' && (
            <Input
              label="Cheque Number"
              placeholder="Cheque number"
              value={form_data.reference_number}
              onChange={(e) => handle_change('reference_number', e.target.value)}
              icon_left={<Hash size={18} />}
              error={errors.reference_number}
              is_required
            />
          )}

          {form_data.payment_method === 'bank_transfer' && (
            <Input
              label="Bank Reference Number"
              placeholder="Transaction reference"
              value={form_data.reference_number}
              onChange={(e) => handle_change('reference_number', e.target.value)}
              icon_left={<Hash size={18} />}
              error={errors.reference_number}
              is_required
              helper_text="Bank transaction or reference number"
            />
          )}

          {(form_data.payment_method === 'cash' || form_data.payment_method === 'card') && (
            <Input
              label="Reference Number (Optional)"
              placeholder="Receipt or reference number"
              value={form_data.reference_number}
              onChange={(e) => handle_change('reference_number', e.target.value)}
              icon_left={<Hash size={18} />}
            />
          )}
        </div>

        {/* Notes */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Additional Notes
          </h3>

          <div>
            <textarea
              value={form_data.notes}
              onChange={(e) => handle_change('notes', e.target.value)}
              placeholder="Any additional notes about this payment..."
              rows={3}
              className="w-full px-4 py-3 border border-light-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all resize-none bg-white text-charcoal"
            />
            <p className="mt-1.5 text-xs text-medium-gray">
              Optional: Add any relevant information about the payment
            </p>
          </div>
        </div>

        {/* Payment Summary */}
        {form_data.amount > 0 && selected_invoice && (
          <div className="p-5 bg-info bg-opacity-5 border border-info rounded-lg">
            <div className="flex gap-3">
              <AlertCircle size={20} className="text-info flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-charcoal mb-3">
                  Payment Summary
                </p>
                <div className="space-y-2 text-sm text-charcoal">
                  <div className="flex justify-between">
                    <span className="text-medium-gray">Payment Amount:</span>
                    <span className="font-semibold">{format_currency(form_data.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-medium-gray">Current Balance:</span>
                    <span className="font-semibold">{format_currency(parseFloat((selected_invoice as any).amount_outstanding))}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-info border-opacity-20">
                    <span className="font-semibold">Remaining Balance:</span>
                    <span className="font-bold text-forest-green">
                      {format_currency(Math.max(0, parseFloat((selected_invoice as any).amount_outstanding) - form_data.amount))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info Message */}
        <div className="p-4 bg-off-white border border-light-gray rounded-lg">
          <div className="flex gap-3">
            <FileText size={20} className="text-medium-gray flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-charcoal mb-1">
                Payment Receipt
              </p>
              <p className="text-xs text-medium-gray">
                A payment receipt will be automatically generated after recording this payment. You can view and print it from the payments list.
              </p>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};